import ComingSoon from "@/components/homepage/ComingSoon";
import { RoundCard } from "@/components/homepage/RoundCard";
import { SelectLeague } from "@/components/homepage/SelectLeague";
import SelectRoundTab from "@/components/homepage/SelectRoundTab";
import SelectSport from "@/components/homepage/SelectSport";
import { SelectSortSport } from "@/components/homepage/SortSport";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandleError } from "@/hooks/useHandleError";
import { useHomePageStore, useSkipPrizesStore } from "@/store";
import { useSoccerRounds } from "@roman/shared/hooks/soccer/useSoccerRounds";
import { minBy } from "lodash";
import { type HTMLAttributes, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HIGH_PRIZE_POOL, LEAGUES, type League } from "./constant";
import { getSortedRounds } from "./utils";

// FAQデータ
const faqs = [
  {
    question: "ポイントはどうやってためますか？",
    answer: "ログインボーナスやアプリ内ミッションをクリアすると無料でポイントを獲得できます。",
  },
  {
    question: "エントリー方法を教えてください。",
    answer: "大会一覧（スロット）から参加したい試合を選び、所定のポイントを消費してエントリーします。",
  },
  {
    question: "何人の選手を選択しますか？",
    answer: "基本は 5 人ですが、大会によっては 11 人を選ぶフォーマットもあります。",
  },
  {
    question: "キャプテン選択とは何ですか？",
    answer: "選んだメンバーの中から 1 人をキャプテンに指定します。キャプテンの活躍は他の選手より高く評価されます。",
  },
  {
    question: "活躍ポイント（FP）はどう計算されますか？",
    answer: "ゴールやアシストなど、公式スタッツに基づくプレーごとに定められた配点表で自動計算されます。",
  },
  {
    question: "難易度「ビギナー」「スタンダード」「エキスパート」の違いは？",
    answer:
      "難易度が上がるほど必要ポイントが多くなり、入賞時にもらえる賞金・ボーナスポイントが大きく設定されています。",
  },
  {
    question: "リアルタイム反映とは？",
    answer: "試合中にスタッツが更新されると、数分以内に FP とランキングに反映されます。",
  },
  {
    question: "ランキングはいつ確定しますか？",
    answer: "試合終了後、公式スタッツが確定した時点で最終ランキングと報酬が確定します。",
  },
];

// FAQアコーディオンコンポーネント
const FAQAccordion = ({
  faqs,
}: {
  faqs: { question: string; answer: string }[];
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <div className="bg-white px-8 py-4">
      {faqs.map((faq, idx) => (
        <div key={idx}>
          <button
            type="button"
            className="flex items-center w-full py-4 text-left font-medium focus:outline-none hover:bg-gray-50 transition"
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            aria-expanded={openIndex === idx}
            aria-controls={`faq-panel-${idx}`}
          >
            <span className="mr-2 text-gray-500">{openIndex === idx ? "▼" : "▶"}</span>
            {faq.question}
          </button>
          {openIndex === idx && (
            <div id={`faq-panel-${idx}`} className="pl-10 pr-4 pb-4 text-gray-700 animate-fadein">
              {faq.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const Tournaments = () => {
  const selectedLeague = useHomePageStore((state) => state.selectedLeague);
  const setSelectedSortType = useHomePageStore((state) => state.setSelectedSortType);
  const selectedSortType = useHomePageStore((state) => state.selectedSortType);

  const selectedSport = useHomePageStore((state) => state.selectedSport);
  const setSelectedSport = useHomePageStore((state) => state.setSelectedSport);

  const selectedTab = useHomePageStore((state) => state.selectedTab);
  const setSelectedTab = useHomePageStore((state) => state.setSelectedTab);

  const { skipPrizes } = useSkipPrizesStore();

  const navigate = useNavigate();

  const handleError = useHandleError("ラウンドの取得に失敗しました");

  const { data, isLoading } = useSoccerRounds("FreePoints", handleError, "ja");

  const filteredRounds = useMemo(() => {
    switch (selectedTab.type) {
      case "all":
        if (selectedLeague.seasonId === "all") {
          return data.rounds ?? [];
        }

        return data.rounds.filter((round) => round.seasonId === selectedLeague.seasonId) || [];
      case "popular":
        return (
          data.rounds
            .filter((round) => {
              if (selectedLeague.seasonId === "all") {
                return Number.parseInt(round.prizePool) >= HIGH_PRIZE_POOL;
              }
              return round.seasonId === selectedLeague.seasonId && Number.parseInt(round.prizePool) >= HIGH_PRIZE_POOL;
            })
            .sort((a, b) =>
              a.entryCloseTime === b.entryCloseTime
                ? Number.parseInt(b.prizePool) - Number.parseInt(a.prizePool)
                : a.entryCloseTime - b.entryCloseTime,
            ) || []
        );
      default:
        return [];
    }
  }, [data.rounds, selectedLeague, selectedTab]);

  const sortedRounds = getSortedRounds(filteredRounds, selectedSortType);

  const activeLeaguesMap = useMemo(() => {
    const activeLeagues = LEAGUES.filter((league) => {
      return data.rounds.some((round) => round.seasonId === league.seasonId);
    });

    return activeLeagues.reduce(
      (acc, league) => {
        acc[league.seasonId] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [data.rounds]);

  const sortedLeagues = useMemo(() => {
    const [all, ...leagues] = LEAGUES;

    const activeLeagues = leagues.map((league) => {
      const rounds = data.rounds.filter((round) => round.seasonId === league.seasonId);
      return {
        ...league,
        entryCloseTime: minBy(rounds, "entryCloseTime")?.entryCloseTime ?? Number.POSITIVE_INFINITY,
      };
    });

    activeLeagues.sort((a, b) => a.entryCloseTime - b.entryCloseTime);

    return [all].concat(activeLeagues).map(({ seasonId, name }) => ({
      seasonId,
      name,
    })) as League[];
  }, [data.rounds]);

  return (
    <div className="w-full bg-white">
      <SelectSport selectedSport={selectedSport} setSelectedSport={setSelectedSport} />
      {selectedSport.id === 1 && (
        <SelectLeague
          selectedLeague={selectedLeague}
          sortedLeagues={sortedLeagues}
          activeLeaguesMap={activeLeaguesMap}
        />
      )}
      <div className="flex justify-between items-center p-[15px]">
        <p className="text-black font-medium">大会一覧</p>
        <SelectSortSport sortBy={selectedSortType} setSortBy={setSelectedSortType} />
      </div>
      <SelectRoundTab selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      {isLoading ? (
        <LoadingSkeleton />
      ) : selectedSport.id === 1 && sortedRounds.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 bg-blue w-full gap-4 p-4 ">
          {sortedRounds.map((round) => (
            <RoundCard
              key={round.id}
              round={round}
              onClick={() => {
                const targetLink = skipPrizes ? "select-players" : "prizes";
                navigate(`/rounds/${round.id}/${targetLink}`);
              }}
            />
          ))}
        </div>
      ) : (
        <div>
          <ComingSoon />
        </div>
      )}
      <div className="flex flex-col items-center">
        <div className="bg-secondary w-full mx-auto flex items-center">
          <img src="/assets/top-thumba-logo.svg" alt="Main Content Background" className="absolute" />
          <div className="text-center text-white flex-1">
            <h1 className="text-xl sm:text-2xl font-bold my-4">サンバ | ファンタジースポーツをはじめるなら</h1>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 py-8">
          <section className="px-8">
            <h2 className="text-xl sm:text-2xl font-bold my-4">サンバとは？</h2>
            <p>
              サンバは、実際の試合データを使って選手の活躍を予想し、
              <em>活躍ポイント（FP）</em>
              の合計で順位を競うファンタジースポーツと ポイ活を組み合わせたサービスです。
              <br />
              ミッションなどでためたポイントを使って大会にエントリーし、
              上位に入賞すると賞金や追加ポイントが獲得できます。
            </p>
          </section>

          <section className="w-full mx-auto px-4 sm:px-8">
            <h2 className="text-xl sm:text-2xl font-bold my-4">ファンタジースポーツとは？</h2>
            <p>
              試合の成績に応じて選手へポイントが付与されます。
              <br />
              活躍しそうな選手を選び、自分だけのチームを編成しましょう！
              <br />
              チーム合計ポイントで他ユーザーと競い合い、上位に入れば賞金が獲得できるスポーツ予想ゲームです。
            </p>
          </section>
        </div>

        <section className="mt-8 w-full">
          <div className="bg-secondary w-full mx-auto flex items-center">
            <img src="/assets/top-thumba-logo.svg" alt="Main Content Background" className="absolute" />
            <div className="text-center text-white flex-1">
              <h2 className="text-xl sm:text-2xl font-bold my-4">よくある質問（FAQ）</h2>
            </div>
          </div>
          <FAQAccordion faqs={faqs} />
        </section>
      </div>
    </div>
  );
};

export const LoadingSkeleton = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-4 p-4" {...props}>
      {[...Array(4)].map((_, index) => (
        <div key={index} className="flex min-w-[250px] flex-col rounded-lg text-base w-full bg-white p-1">
          <Skeleton className="h-[200px] w-full rounded-xl" />
          <div className="mt-2 flex items-center flex-row justify-start">
            <Skeleton className="w-[62px] h-[28px]" />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <Skeleton className="w-[62px] h-[28px]" />
            <Skeleton className="w-[62px] h-[28px]" />
          </div>
        </div>
      ))}
    </div>
  );
};
